import { RequestCheckoutState, SelectedAddon, SelectedOffer } from '@shared/types';
import { Fragment, FunctionComponent, ReactNode, useMemo } from 'react';
import { CheckoutSummary, CheckoutSummaryProps } from '..';
import { PriceSummary } from '../BookingCheckoutSummary/PriceSummary';
import styles from './styles.module.scss';
import { TotalSummary } from './TotalSummary';
import { useCostSummary } from '../../data/useCostSummary';
import { formatPrice } from './formatPrice';
import { IconSummary } from '../IconSummary';
import {
    CalendarDayLightIcon,
    CameraHomeLightIcon,
    ClockLightIcon,
    UserGroupLightIcon,
    UserLightIcon,
} from '../../../Icons';
import { useRequestCheckoutContext } from '../../CheckoutContext/RequestContext';
import {
    shortDateFormatOptions,
    shortDateTimeFormatOptions,
} from '../../../../services/dateFormats';
import HousePersonLeaveLightIcon from '../../../Icons/HousePersonLeaveLightIcon';
import HousePersonReturnLightIcon from '../../../Icons/HousePersonReturnLightIcon';
import { Cancellation } from '../Cancellation';
import { PublicOnRequestCourse } from '@cademy-package/sdk-component-types';
import { LocalDate } from '../../../LocalDate';
import { Temporal } from 'temporal-polyfill';
import { TimezoneDate } from '../../../TimezoneDate';

const getDurationLabel = (durationInMinutes: number): string => {
    if (durationInMinutes === 0) {
        return '';
    }
    if (durationInMinutes <= 1) {
        return `1 Minute`;
    }
    if (durationInMinutes < 60) {
        return `${durationInMinutes} Minutes`;
    }
    if (durationInMinutes === 60) {
        return '1 Hour';
    }
    if (durationInMinutes < 60 * 24) {
        const hours = Math.floor(durationInMinutes / 60);
        const remaining = durationInMinutes % 60;
        return `${hours} Hours ${getDurationLabel(remaining)}`;
    }
    if (durationInMinutes === 60 * 24) {
        return '1 Day';
    }
    const days = Math.floor(durationInMinutes / (60 * 24));
    const remaining = durationInMinutes % (60 * 24);
    return `${days} Days ${getDurationLabel(remaining)}`;
};

const PreferredDatesSummary: FunctionComponent<{ preferredDates: Array<string> }> = ({
    preferredDates,
}) => {
    const potentiallyEmptyDates = preferredDates;
    if (!potentiallyEmptyDates) {
        return null;
    }

    const dates = potentiallyEmptyDates.filter((date) => date !== '');
    if (dates.length === 0) {
        return null;
    }

    if (dates.length === 1) {
        return (
            <IconSummary Icon={CalendarDayLightIcon}>
                <TimezoneDate
                    date={Temporal.ZonedDateTime.from(dates[0])}
                    format={shortDateFormatOptions}
                />
            </IconSummary>
        );
    }
    if (dates.length === 2) {
        return (
            <IconSummary Icon={CalendarDayLightIcon}>
                <TimezoneDate
                    date={Temporal.ZonedDateTime.from(dates[0])}
                    format={shortDateFormatOptions}
                />{' '}
                or{' '}
                <TimezoneDate
                    date={Temporal.ZonedDateTime.from(dates[1])}
                    format={shortDateFormatOptions}
                />
            </IconSummary>
        );
    }
    return (
        <IconSummary Icon={CalendarDayLightIcon}>
            {dates.map((dateString, index) => {
                if (!dateString) return null;
                const date = Temporal.ZonedDateTime.from(dateString);
                if (index === dates.length - 1) {
                    return (
                        <Fragment key={index}>
                            {' '}
                            or <TimezoneDate date={date} format={shortDateFormatOptions} />
                        </Fragment>
                    );
                }
                return (
                    <Fragment key={index}>
                        <TimezoneDate date={date} format={shortDateFormatOptions} />,{' '}
                    </Fragment>
                );
            })}
        </IconSummary>
    );
};

const AvailableDateSummary: FunctionComponent<{ availableDate: string }> = ({ availableDate }) => {
    const formattedDate = useMemo(() => {
        if (!availableDate) {
            return '';
        }
        return (
            <LocalDate
                date={Temporal.Instant.from(availableDate)}
                format={shortDateTimeFormatOptions}
            />
        );
    }, [availableDate]);
    if (!formattedDate) {
        return null;
    }
    return <IconSummary Icon={CalendarDayLightIcon}>{formattedDate}</IconSummary>;
};

export type RequestCheckoutSummaryProps = {
    course: PublicOnRequestCourse;
    checkoutState: RequestCheckoutState;
};
export const RequestCheckoutSummary: FunctionComponent<RequestCheckoutSummaryProps> = ({
    course,
    checkoutState,
}) => {
    const { educator, isInstantBooking } = useRequestCheckoutContext();
    const hidePricing = useMemo(
        () => checkoutState.origin !== 'marketplace' && educator.feature_flags?.hide_pricing,
        [checkoutState.origin, educator.feature_flags]
    );

    const offers = useMemo(() => {
        if (checkoutState.stepState.delivery.state?.pricing.type !== 'offers') {
            return [];
        }
        return Object.entries(checkoutState.stepState.delivery.state.pricing.offers).map(
            ([offerId, quantity]): SelectedOffer => {
                return {
                    offerId,
                    quantity,
                };
            }
        );
    }, [checkoutState]);

    const addons = useMemo(() => {
        if (checkoutState.stepState.delivery.state?.pricing.type !== 'offers') {
            return [];
        }
        return Object.entries(checkoutState.stepState.delivery.state.pricing.addOns).map(
            ([addonId, quantity]): SelectedAddon => {
                return {
                    addonId,
                    quantity,
                };
            }
        );
    }, [checkoutState]);

    const selectedDuration = useMemo(() => {
        const selectedDurationId = checkoutState.stepState.delivery.state?.selectedDurationId;
        if (!selectedDurationId) {
            return null;
        }
        const duration = course.duration_pricing.find((duration) => {
            return duration._id === selectedDurationId;
        });
        return duration || null;
    }, [course, checkoutState]);

    const requestedSpaces = useMemo(() => {
        return checkoutState.stepState.delivery.state?.requestedSpaces || 0;
    }, [checkoutState]);

    const { data: costSummary } = useCostSummary(
        {
            courseId: checkoutState.courseId,
            offers: offers,
            addons: addons,
            origin: checkoutState.origin,
            extras:
                selectedDuration && selectedDuration.extra_cost
                    ? [
                          {
                              label: selectedDuration.label || String(selectedDuration.duration),
                              cost: selectedDuration.extra_cost,
                              quantity: requestedSpaces,
                          },
                      ]
                    : [],
        },
        {
            keepPreviousData: true,
        }
    );

    const datesSummary = useMemo((): ReactNode => {
        if (checkoutState.stepState.dates.status !== 'complete') {
            return null;
        }
        switch (checkoutState.stepState.dates.state.type) {
            case 'preferred dates':
                return (
                    <PreferredDatesSummary
                        preferredDates={checkoutState.stepState.dates.state.preferredDates}
                    />
                );
            case 'available date':
                return (
                    <AvailableDateSummary
                        availableDate={checkoutState.stepState.dates.state.availableDate}
                    />
                );
        }
    }, [checkoutState]);

    const datesSection = useMemo(() => {
        if (!datesSummary) {
            return null;
        }
        if (checkoutState.stepState.dates.status !== 'complete') {
            return null;
        }
        return (
            <div>
                <span>
                    {checkoutState.stepState.dates.state.type === 'preferred dates'
                        ? 'Preferred dates'
                        : 'Selected date'}
                </span>
                {datesSummary}
            </div>
        );
    }, [checkoutState.stepState.dates, datesSummary]);

    const spacesSummary = useMemo((): ReactNode => {
        const requestedSpaces = checkoutState.stepState.delivery.state?.requestedSpaces;
        if (!requestedSpaces) {
            return null;
        }
        if (requestedSpaces === 1) {
            return <IconSummary Icon={UserLightIcon}>1 space</IconSummary>;
        }
        return <IconSummary Icon={UserGroupLightIcon}>{requestedSpaces} spaces</IconSummary>;
    }, [checkoutState]);

    const deliverySummary = useMemo((): ReactNode => {
        const delivery = checkoutState.stepState.delivery.state?.delivery;
        if (!delivery) {
            return null;
        }
        switch (delivery.type) {
            case 'Online': {
                return <IconSummary Icon={CameraHomeLightIcon}>Delivered online</IconSummary>;
            }
            case 'Educator travels': {
                return (
                    <IconSummary Icon={HousePersonReturnLightIcon}>
                        I will host {educator.name}
                    </IconSummary>
                );
            }
            case 'Student travels': {
                return (
                    <IconSummary Icon={HousePersonLeaveLightIcon}>
                        I will travel to {educator.name}
                    </IconSummary>
                );
            }
        }
    }, [educator, checkoutState]);

    const durationSummary = useMemo((): ReactNode => {
        if (!selectedDuration) {
            return null;
        }
        const durationString = getDurationLabel(selectedDuration.duration);
        if (!selectedDuration.label) {
            return <IconSummary Icon={ClockLightIcon}>{durationString}</IconSummary>;
        }
        return (
            <IconSummary Icon={ClockLightIcon}>
                {selectedDuration.label} - {durationString}
            </IconSummary>
        );
    }, [selectedDuration]);

    const deliverySection = useMemo((): ReactNode => {
        return (
            <>
                {spacesSummary}
                {deliverySummary}
                {durationSummary}
                <Cancellation educator={educator} course={course} />
            </>
        );
    }, [spacesSummary, deliverySummary, durationSummary, educator, course]);

    const offersSection = useMemo((): ReactNode => {
        if (!costSummary) {
            return null;
        }
        if (costSummary.total_cost === 0 || hidePricing) {
            return null;
        }
        const { offerSummary, addOnSummary, extraSummary } = costSummary.summary_items;
        if (!offerSummary.length && !addOnSummary.length && !extraSummary.length) {
            return null;
        }
        return (
            <ul className={styles.costSection}>
                {offerSummary.map((offer, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={offer.text}
                                price={offer.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
                {addOnSummary.map((addon, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={addon.text}
                                price={addon.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
                {extraSummary.map((extra, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={extra.text}
                                price={extra.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
            </ul>
        );
    }, [costSummary, course.currency, hidePricing]);

    const additionalChargesSection = useMemo((): ReactNode => {
        if (!costSummary || hidePricing) {
            return null;
        }
        if (course.pricing_option.type !== 'price-offers') {
            return null;
        }
        const { vatSummary } = costSummary.summary_items;
        const { cademy_fees, fees_absorbed } = costSummary;
        if (!vatSummary.length && (!cademy_fees || fees_absorbed === true)) {
            return null;
        }
        return (
            <ul className={styles.costSection}>
                {vatSummary.map((vat, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={vat.text}
                                price={vat.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
                {cademy_fees && fees_absorbed !== true ? (
                    <li>
                        <PriceSummary label="Fees" price={cademy_fees} currency={course.currency} />
                    </li>
                ) : null}
            </ul>
        );
    }, [costSummary, course.currency, course.pricing_option.type, hidePricing]);

    const totalSection = useMemo(() => {
        if (hidePricing) {
            return null;
        }
        return <TotalSummary costSummary={costSummary} />;
    }, [costSummary, hidePricing]);

    const sections = useMemo((): CheckoutSummaryProps['summarySections'] => {
        return [
            deliverySection,
            datesSection,
            offersSection,
            additionalChargesSection,
            totalSection,
        ];
    }, [deliverySection, datesSection, offersSection, additionalChargesSection, totalSection]);

    const tagLine = useMemo(() => {
        return (
            <>
                {spacesSummary}
                {datesSummary}
            </>
        );
    }, [spacesSummary, datesSummary]);

    const totalCost = useMemo(() => {
        if (isInstantBooking) {
            return costSummary?.total_cost || 0;
        }
        if (course.pricing_option.type === 'free') {
            return 'Free';
        }
        if (course.pricing_option.type === 'price-on-enquiry') {
            return 'Price on enquiry';
        }
        if (course.pricing_option.type === 'price-range') {
            const { minimumPrice, maximumPrice } = course.pricing_option;
            if (minimumPrice === maximumPrice) {
                return formatPrice(minimumPrice);
            }
            return `${formatPrice(minimumPrice)} - ${formatPrice(maximumPrice)}`;
        }
        return costSummary?.total_cost || 0;
    }, [course, costSummary, isInstantBooking]);

    return (
        <CheckoutSummary
            course={course}
            taglineDetail={tagLine}
            summarySections={sections}
            totalCost={totalCost}
        />
    );
};
